import type { LucideIcon } from "lucide-react";

import { MarcaCompleta } from "./Marca.tsx";

/**
 * El lado izquierdo de las pantallas públicas.
 *
 * Vive aparte porque lo usan dos —entrar y aceptar una invitación— y duplicarlo
 * garantizaba que se separaran: el día que cambie el argumento del producto,
 * cambiaría en una y no en la otra, y la que se quedaría vieja es la que ve
 * alguien que llega por primera vez.
 *
 * Lo que NO se comparte es el texto. A `/entrar` llega quien ya es cliente; a
 * `/invitacion`, alguien a quien acaban de mandarle un enlace y que puede no
 * haber oído hablar de esto nunca. Mismo sitio, mismas piezas, otras palabras.
 */
export interface Argumento {
  icono: LucideIcon;
  titulo: string;
  texto: string;
}

export function PanelPublico({
  titular,
  entrada,
  argumentos,
  pie,
}: {
  titular: string;
  entrada: string;
  argumentos: Argumento[];
  pie: string;
}) {
  return (
    // Color fijo, no tokens: este panel es siempre oscuro con texto blanco. Si
    // siguiera el tema, en modo oscuro la marca se aclara y el texto blanco se
    // quedaría sin contraste sobre ella.
    <aside
      className="relative hidden overflow-hidden p-12 text-white lg:flex lg:flex-col lg:justify-between"
      style={{ backgroundColor: "oklch(32% 0.06 195)" }}
    >
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0"
        style={{
          backgroundImage:
            "radial-gradient(120% 85% at 10% -10%, oklch(58% 0.12 188 / .85), transparent 62%), radial-gradient(95% 75% at 105% 105%, oklch(38% 0.15 265 / .9), transparent 60%)",
        }}
      />
      <div className="relative">
        <MarcaCompleta variante="blanco" />
      </div>

      <div className="relative max-w-md space-y-10">
        <div className="space-y-4">
          <h1 className="text-4xl leading-[1.1] font-semibold tracking-tight text-balance">
            {titular}
          </h1>
          <p className="text-[15px] leading-relaxed text-white/75">{entrada}</p>
        </div>

        <ul className="space-y-5">
          {argumentos.map(({ icono: Icono, titulo, texto }) => (
            <li key={titulo} className="flex gap-3.5">
              <span className="grid size-9 shrink-0 place-items-center rounded-xl bg-white/12 ring-1 ring-white/15">
                <Icono className="size-4.5" aria-hidden />
              </span>
              <div className="space-y-0.5">
                <p className="text-sm font-semibold">{titulo}</p>
                <p className="text-sm leading-relaxed text-white/65">{texto}</p>
              </div>
            </li>
          ))}
        </ul>
      </div>

      <p className="relative text-xs text-white/45">{pie}</p>
    </aside>
  );
}
